import get from "lodash/get";
import React, { useMemo } from "react";
import { Button, Modal, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";

import IndicatorForm from "~/components/Forms/IndicatorForm";
import Wrapper from "~/shared/ui/Wrapper";
import { STATUS_TYPES } from "~/constans/statuses";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { getIndicatorName } from "~/libs/getData";
import {
  useDeleteIndicatorMutation,
  useGetIndicatorsQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2, useTableSort } from "~/shared/ui/TableV2";

import IndicatorsFilter from "./Filter";
import {
  getIndicatorsColumns,
  getIndicatorsSortableApiKeys,
  indicatorsSortFieldForQuery,
} from "./model/tableAdapter";

import s from "./Indicators.less";

const indicatorFormId = "IndicatorForm";

const Indicators = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission } = usePermissions();
  const setData = useSetLocationData();
  // Открытие/закрытие модалок — через replace, чтобы не плодить историю браузера
  const setDataReplace = useSetLocationData(true);
  const {
    page,
    count,
    searchField,
    searchText,
    sortField,
    sortType: sortTypeRaw,
    openModal,
    currentId,
  } = useGetLocationData();

  const sortableApiKeys = useMemo(() => {
    const lang = localStorage.getItem("language");
    return getIndicatorsSortableApiKeys(lang);
  }, [i18n.language]);

  const { sortType: normalizedSortType, onSortChange } = useTableSort({
    sortField,
    sortType: sortTypeRaw,
    sortableColumns: sortableApiKeys,
    setData,
  });

  const tableSortField =
    sortField && sortableApiKeys.includes(sortField) ? sortField : undefined;

  const querySortField = indicatorsSortFieldForQuery(sortField);

  const columns = useMemo(() => {
    const lang = localStorage.getItem("language");
    return getIndicatorsColumns(t, sortField, normalizedSortType, lang);
  }, [t, sortField, normalizedSortType, i18n.language]);

  const [deleteIndicatorMutation, deletedIndicator] =
    useDeleteIndicatorMutation({
      variables: {
        id: currentId,
      },
      onCompleted() {
        refetch();
      },
    });

  const { data, refetch, loading } = useGetIndicatorsQuery({
    variables: {
      searchText,
      sortType: querySortField ? normalizedSortType : undefined,
      searchField,
      sortField: querySortField,
      page,
      count,
    },
    fetchPolicy: "no-cache",
  });

  const indicators = get(data, "IndicatorGetter.getIndicators.items", []).map(
    (indicator) => {
      const { nameRu, nameEn, shortNameRu, shortNameEn } = indicator;
      return {
        ...indicator,
        name: getIndicatorName(indicator),
        nameEn: nameEn || shortNameEn,
        nameRu: nameRu || shortNameRu,
      };
    },
  );

  const total = get(data, "IndicatorGetter.getIndicators.total", 0);
  const selectedIndicator = indicators.find((i) => i.id === currentId);
  // Для удалённого индикатора сабмит модалки работает как восстановление
  // (раньше эта логика жила внутри IndicatorForm)
  const isRestore =
    !!selectedIndicator && selectedIndicator.status === STATUS_TYPES.REMOVED;

  const formModalIsOpen =
    openModal === "Add" ||
    ((openModal === "Edit" || openModal === "Restore") && !!selectedIndicator);

  const { handleCount, handlePage } = usePaginationParams();

  const handleCloseModal = () => {
    setDataReplace("openModal", "", "currentId", "", "openChildModal", "");
  };

  const deleteIndicator = () => {
    deleteIndicatorMutation();
    handleCloseModal();
  };

  const openAddModal = () => {
    setDataReplace("openModal", "Add");
  };

  return (
    <Wrapper.Content
      title={t("indicators.indicators")}
      filterComponent={<IndicatorsFilter />}
      controlComponent={
        hasPermission("indicator.set") ? (
          <Button
            onClick={openAddModal}
            size={"middle"}
            icon={<PlusIcon size={16} />}
          >
            {t("ui.add")}
          </Button>
        ) : undefined
      }
    >
      <TableV2
        wrapperClassName={s.tableWrapper}
        columns={columns}
        dataSource={indicators}
        loading={loading}
        rowKey="id"
        scroll={{ x: "max-content" }}
        sortableColumns={sortableApiKeys}
        sortField={tableSortField}
        sortType={tableSortField ? normalizedSortType : undefined}
        onSortChange={onSortChange}
        {...(hasPermission("indicator.set") && {
          onRowEdit: (record) =>
            setDataReplace("openModal", "Edit", "currentId", record.id),
        })}
        {...(hasPermission("indicator.delete") && {
          onRowDelete: (record) =>
            setDataReplace("openModal", "Sure", "currentId", record.id),
        })}
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={total}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />
      <Modal
        open={formModalIsOpen}
        title={
          openModal === "Add"
            ? t("indicators.indicator_add")
            : t("indicators.indicator_edit")
        }
        onCancel={handleCloseModal}
        closable
        centered
        destroyOnHidden
        okButton={{
          htmlType: "submit",
          form: indicatorFormId,
          children: isRestore ? t("ui.recover") : t("ui.save"),
          disabled: isRestore && !hasPermission("indicator.restore"),
        }}
      >
        {openModal === "Add" ? (
          <IndicatorForm
            formId={indicatorFormId}
            setCloseModal={handleCloseModal}
            refetch={refetch}
          />
        ) : (
          !!selectedIndicator && (
            <IndicatorForm
              formId={indicatorFormId}
              setCloseModal={handleCloseModal}
              refetch={refetch}
              ind={selectedIndicator}
            />
          )
        )}
      </Modal>
      <Modal
        open={openModal === "Sure"}
        title={t("indicators.indicator_del")}
        onCancel={handleCloseModal}
        closable
        centered
        destroyOnHidden
        cancelButton={{ children: t("ui.cancel") }}
        okButton={{
          children: t("ui.delete"),
          onClick: deleteIndicator,
          loading: deletedIndicator.loading,
        }}
      />
    </Wrapper.Content>
  );
};

export default Indicators;
